import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useOutlet } from "@/contexts/OutletContext";

export default function Menu() {
  const { outlets } = useOutlet();
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("items");

  const [catOpen, setCatOpen] = useState(false);
  const [catEdit, setCatEdit] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", sort_order: 0 });

  const [itOpen, setItOpen] = useState(false);
  const [itEdit, setItEdit] = useState(null);
  const [itForm, setItForm] = useState({
    name: "", code: "", category_id: "", price: 0, gst_rate: 5, type: "veg", kitchen: "kitchen",
    description: "", image_url: "", active: true, outlet_prices: {},
  });

  const loadQuickStart = async () => {
    if (!window.confirm("Add a standard Indian restaurant menu (~70 items)? Existing items with same names will be skipped.")) return;
    try {
      const { data } = await api.post("/menu/quick-start", {});
      toast.success(`Added ${data.categories_added} categories · ${data.items_added} items · skipped ${data.skipped} duplicates`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/categories"), api.get("/menu-items")]);
    setCats(c.data);
    setItems(i.data);
  };
  useEffect(() => { load(); }, []);

  // Category handlers
  const startNewCat = () => { setCatEdit(null); setCatForm({ name: "", sort_order: cats.length + 1 }); setCatOpen(true); };
  const startEditCat = (c) => { setCatEdit(c); setCatForm({ name: c.name, sort_order: c.sort_order }); setCatOpen(true); };
  const saveCat = async () => {
    try {
      if (catEdit) await api.put(`/categories/${catEdit.id}`, catForm);
      else await api.post("/categories", catForm);
      toast.success("Saved"); setCatOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delCat = async (c) => {
    if (!window.confirm(`Delete category ${c.name}?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Item handlers
  const startNewIt = () => {
    setItEdit(null);
    setItForm({ name: "", code: "", category_id: cats[0]?.id || "", price: 0, gst_rate: 5, type: "veg", kitchen: "kitchen", description: "", image_url: "", active: true, outlet_prices: {} });
    setItOpen(true);
  };
  const startEditIt = (i) => {
    setItEdit(i);
    setItForm({ name: i.name, code: i.code || "", category_id: i.category_id, price: i.price, gst_rate: i.gst_rate, type: i.type, kitchen: i.kitchen || "kitchen", description: i.description || "", image_url: i.image_url || "", active: i.active, outlet_prices: i.outlet_prices || {} });
    setItOpen(true);
  };
  const saveIt = async () => {
    try {
      const cleanedPrices = {};
      Object.entries(itForm.outlet_prices || {}).forEach(([k, v]) => {
        const n = Number(v);
        if (n > 0) cleanedPrices[k] = n;
      });
      const payload = { ...itForm, price: Number(itForm.price), gst_rate: Number(itForm.gst_rate), variants: [], modifiers: [], outlet_prices: cleanedPrices };
      if (itEdit) await api.put(`/menu-items/${itEdit.id}`, payload);
      else await api.post("/menu-items", payload);
      toast.success("Saved"); setItOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delIt = async (i) => {
    if (!window.confirm(`Delete ${i.name}?`)) return;
    try { await api.delete(`/menu-items/${i.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  return (
    <div className="p-6 sm:p-8" data-testid="menu-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Menu Management</h1>
        <p className="text-sm text-[#7A7873] mt-1">Organize categories, dishes, prices and GST rates.</p>
      </div>

      {items.length < 10 && (
        <div className="mb-4 bg-[#FFF6E8] border border-[#F4D58B] rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid="quickstart-banner">
          <Sparkles size={18} className="text-[#7A5A18] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-[#7A5A18]">Quick Start: Load standard Indian menu</div>
            <div className="text-xs text-[#7A5A18]">Adds ~70 popular veg + non-veg dishes across 8 categories (starters, mains, breads, biryani, Chinese, beverages, desserts). Skips duplicates.</div>
          </div>
          <Button onClick={loadQuickStart} className="bg-[#7A5A18] hover:bg-[#5C440F]" data-testid="quickstart-btn">
            <Sparkles size={14} className="mr-1.5" /> Load Standard Menu
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#EBE8E2]">
          <TabsTrigger value="items" data-testid="tab-items">Menu Items</TabsTrigger>
          <TabsTrigger value="cats" data-testid="tab-cats">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={startNewIt} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-item-btn">
              <Plus size={16} className="mr-1.5" /> Add Item
            </Button>
          </div>
          <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-right px-4 py-2.5">Price</th>
                  <th className="text-right px-4 py-2.5">GST%</th>
                  <th className="text-right px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="border-t border-[#EBE8E2]" data-testid={`menu-row-${i.id}`}>
                    <td className="px-4 py-2.5 font-medium text-[#2D2C2A]">{i.name}</td>
                    <td className="px-4 py-2.5 text-[#7A7873]">{catName(i.category_id)}</td>
                    <td className="px-4 py-2.5 capitalize">{i.type}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{inr(i.price)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{i.gst_rate}%</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${i.active ? "bg-[#E5EBE3] text-[#6A7F66]" : "bg-[#F4F2EE] text-[#7A7873]"}`}>
                        {i.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEditIt(i)} data-testid={`edit-item-${i.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => delIt(i)}>
                        <Trash2 size={13} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="cats" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={startNewCat} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-cat-btn">
              <Plus size={16} className="mr-1.5" /> Add Category
            </Button>
          </div>
          <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-right px-4 py-2.5">Order</th>
                  <th className="text-right px-4 py-2.5">Items</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {cats.map((c) => (
                  <tr key={c.id} className="border-t border-[#EBE8E2]">
                    <td className="px-4 py-2.5 font-medium">{c.name}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{c.sort_order}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{items.filter((i) => i.category_id === c.id).length}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEditCat(c)}>
                        <Pencil size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => delCat(c)}>
                        <Trash2 size={13} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Category dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{catEdit ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-name-input" /></div>
            <div><Label>Sort order</Label><Input type="number" value={catForm.sort_order} onChange={(e) => setCatForm({ ...catForm, sort_order: Number(e.target.value) })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={saveCat} data-testid="save-cat">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item dialog */}
      <Dialog open={itOpen} onOpenChange={setItOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{itEdit ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <div><Label>Code</Label><Input value={itForm.code} onChange={(e) => setItForm({ ...itForm, code: e.target.value.toUpperCase() })} placeholder="PT01" data-testid="item-code-input" maxLength={8} /></div>
              <div><Label>Name</Label><Input value={itForm.name} onChange={(e) => setItForm({ ...itForm, name: e.target.value })} data-testid="item-name-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={itForm.category_id} onValueChange={(v) => setItForm({ ...itForm, category_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kitchen Routing</Label>
                <Select value={itForm.kitchen} onValueChange={(v) => setItForm({ ...itForm, kitchen: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="bar">Bar</SelectItem>
                    <SelectItem value="tandoor">Tandoor</SelectItem>
                    <SelectItem value="chinese">Chinese</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price (₹)</Label><Input type="number" value={itForm.price} onChange={(e) => setItForm({ ...itForm, price: e.target.value })} /></div>
              <div><Label>GST %</Label><Input type="number" value={itForm.gst_rate} onChange={(e) => setItForm({ ...itForm, gst_rate: e.target.value })} /></div>
              <div>
                <Label>Type</Label>
                <Select value={itForm.type} onValueChange={(v) => setItForm({ ...itForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="veg">Veg</SelectItem>
                    <SelectItem value="non-veg">Non-Veg</SelectItem>
                    <SelectItem value="egg">Egg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Image URL</Label><Input value={itForm.image_url} onChange={(e) => setItForm({ ...itForm, image_url: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea value={itForm.description} onChange={(e) => setItForm({ ...itForm, description: e.target.value })} /></div>

            {outlets.length > 1 && (
              <div className="border border-[#EBE8E2] rounded-md p-3 bg-[#FBFAF8]">
                <div className="text-xs font-semibold text-[#2D2C2A] mb-2">Per-Outlet Price Override (optional)</div>
                <div className="text-[11px] text-[#7A7873] mb-2">Leave blank to use the base price above. Use this when different branches charge different prices.</div>
                <div className="space-y-2">
                  {outlets.map((o) => (
                    <div key={o.id} className="grid grid-cols-[1fr_120px] gap-2 items-center">
                      <span className="text-sm text-[#2D2C2A]">{o.name}</span>
                      <Input
                        type="number"
                        placeholder={`base ₹${itForm.price}`}
                        value={itForm.outlet_prices?.[o.id] ?? ""}
                        onChange={(e) => setItForm({ ...itForm, outlet_prices: { ...(itForm.outlet_prices || {}), [o.id]: e.target.value } })}
                        className="h-8 text-sm font-mono-num"
                        data-testid={`outlet-price-${o.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={saveIt} data-testid="save-item">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
